/**
 * SettingSlider Component
 * Labeled slider with markers for training parameters
 */

import { cn } from "@/lib/utils";

interface SettingSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  displayValue: string;
  subtitle?: string;
  onChange: (value: number) => void;
  markers?: number[];
}

export function SettingSlider({
  label,
  value,
  min,
  max,
  step,
  displayValue,
  subtitle,
  onChange,
  markers = [],
}: SettingSliderProps) {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="rounded-[8px] border border-white/10 bg-[#1a1a1a] p-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-xs text-muted-foreground uppercase tracking-wider">
            {label}
          </h4>
          {subtitle && (
            <p className="text-[10px] text-muted-foreground/60 mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        <span className="text-sm font-mono text-foreground">
          {displayValue}
        </span>
      </div>

      {/* Slider track */}
      <div className="relative h-8">
        {/* Background track */}
        <div className="absolute top-1/2 left-0 right-0 h-1.5 -translate-y-1/2 rounded-full bg-white/10" />

        {/* Filled track */}
        <div
          className="absolute top-1/2 left-0 h-1.5 -translate-y-1/2 rounded-full bg-primary transition-all"
          style={{ width: `${percentage}%` }}
        />

        {/* Markers */}
        {markers.map((marker) => {
          const markerPercent = ((marker - min) / (max - min)) * 100;
          return (
            <div
              key={marker}
              className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-white/20 rounded-full"
              style={{ left: `${markerPercent}%` }}
            />
          );
        })}

        {/* Thumb */}
        <div
          className={cn(
            "absolute top-1/2 -translate-y-1/2 -translate-x-1/2",
            "w-4 h-4 rounded-full bg-primary border-2 border-white",
            "shadow-lg cursor-pointer",
          )}
          style={{ left: `${percentage}%` }}
        />

        {/* Invisible input for interaction */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>

      {/* Min/Max labels */}
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-muted-foreground/60">{min}</span>
        <span className="text-[10px] text-muted-foreground/60">{max}</span>
      </div>
    </div>
  );
}
